import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";

import { isActivePatronRequestSeries } from "@/features/BookingParts/components/BookingInfo/isActivePatronRequestSeries";
import { useFacilityCourseNoun } from "@/features/Facility/hooks/useFacilityCourseNoun";
import {
  recurrenceInputFromPersistedSeries,
  sortSeriesBookingsByDate,
} from "@/features/RecurringRequests/recurrence";
import {
  RecurrenceAvailabilityPreview,
  RecurrenceFormValue,
} from "@/features/RecurringRequests/types";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useFlags } from "@/utils/launchDarkly";
import { Button } from "@whoosh/design-system/src/components/controls";
import {
  CalendarIcon,
  ChevronRightIcon,
  InfoIcon,
  RecurringIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { DetailRow } from "../BookingRequest/components/RequestDetails/DetailRow";
import { RecurringRequestSeriesCard_booking$key } from "./__generated__/RecurringRequestSeriesCard_booking.graphql";
import { RecurringRequestDatesModal } from "./components/RecurringRequestDatesModal/RecurringRequestDatesModal";
import { RecurringRequestModal } from "./components/RecurringRequestModal/RecurringRequestModal";
import {
  formatSeriesEndDate,
  lastRequestedSeriesDate,
  recurrencePatternLabel,
} from "./formatters";

type RecurringRequestSeriesCardProps = {
  bookingRef: RecurringRequestSeriesCard_booking$key | null;
  currentSessionUserClientId: string;
  placement: "details" | "actions";
  pendingRecurrence?: RecurrenceFormValue | null;
  availabilityPreview?: RecurrenceAvailabilityPreview | null;
};

export const RecurringRequestSeriesCard = ({
  bookingRef,
  currentSessionUserClientId,
  placement,
  pendingRecurrence = null,
  availabilityPreview = null,
}: RecurringRequestSeriesCardProps) => {
  const booking = useFragment(
    RecurringRequestSeriesCardFragmentString,
    bookingRef
  );
  const { patronRecurringRequests } = useFlags();
  const [isSeriesOpen, setIsSeriesOpen] = useState(false);
  const [isRecurrenceOpen, setIsRecurrenceOpen] = useState(false);
  const trackEvent = useTrackEvent();
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "BookingSummary">>();

  const sourceDate = booking?.startSlot?.agenda?.date;
  const facility = booking?.startSlot?.agenda?.facility;
  const { singular: courseSingularLabel } = useFacilityCourseNoun(
    facility ?? null
  );
  const clubWeekStartsOn =
    facility?.club?.calendarStartDay === "SUNDAY" ? 0 : 1;
  const maximumEndDate = facility?.patronRecurringRequestMaximumEndDate;
  const series = booking?.bookingSeries;
  const seriesBookings = sortSeriesBookingsByDate(
    series?.bookings?.edges
      ?.map((edge) => edge?.node)
      .filter(
        (seriesBooking): seriesBooking is NonNullable<typeof seriesBooking> =>
          !!seriesBooking
      ) ?? []
  );
  const submittedRequestCount = seriesBookings.length;
  const isRequest = booking?.startSlot?.__typename === "QueueSlot";
  const isStandardBooking = booking?.__typename === "StandardBooking";
  const isOwner = booking?.ownerClient?.id === currentSessionUserClientId;
  const recurringPermission =
    facility?.privateBookingPermissionSet?.membersCanMakeRecurringRequests;
  const isFullyPrivate =
    facility?.club?.supportsPrivate === true &&
    facility?.club?.supportsPublic === false;
  const canManageRecurrence =
    !!patronRecurringRequests &&
    isRequest &&
    isStandardBooking &&
    isOwner &&
    recurringPermission &&
    facility?.type !== "DINING" &&
    isFullyPrivate &&
    !!sourceDate &&
    !!maximumEndDate &&
    maximumEndDate > sourceDate;
  const isPatronRequestSeries = !!series?.recurrenceType;
  const hasActivePersistedSeries = isActivePatronRequestSeries({
    hasSeriesRecurrence: isPatronRequestSeries,
    remainingBookingCount: submittedRequestCount,
  });
  const activeRecurrence =
    pendingRecurrence ?? recurrenceInputFromPersistedSeries(series, sourceDate);

  if (!booking) return null;

  const persistedOccurrences = seriesBookings
    .map((seriesBooking) => ({
      date: seriesBooking.startSlot?.agenda?.date ?? "",
      warnings: [],
    }))
    .filter((occurrence) => !!occurrence.date);
  const unchangedPersistedOccurrences =
    pendingRecurrence && isPatronRequestSeries && sourceDate
      ? persistedOccurrences.filter(
          (occurrence) => occurrence.date < sourceDate
        )
      : [];
  const availableOccurrences =
    pendingRecurrence && availabilityPreview
      ? [...unchangedPersistedOccurrences, ...availabilityPreview.included]
      : availabilityPreview?.included ?? persistedOccurrences;
  const unavailableDates = pendingRecurrence
    ? availabilityPreview?.excluded ?? []
    : [];
  const removedOccurrences = pendingRecurrence
    ? availabilityPreview?.proposedRemovals ?? []
    : [];
  const requestCount =
    pendingRecurrence && availabilityPreview
      ? availableOccurrences.length
      : submittedRequestCount > 0
      ? submittedRequestCount
      : activeRecurrence?.endCondition === "AFTER_OCCURRENCES"
      ? activeRecurrence.occurrenceCount ?? 0
      : 0;
  const totalSeriesDates =
    availableOccurrences.length + unavailableDates.length;
  const endsDate = lastRequestedSeriesDate(
    availableOccurrences.map((occurrence) => occurrence.date)
  );
  const endsDateLabel =
    pendingRecurrence && !availabilityPreview
      ? null
      : formatSeriesEndDate(endsDate);
  const coursePreferences =
    booking.__typename === "StandardBooking"
      ? [...(booking.request?.requestCoursePreferences ?? [])].sort(
          (a, b) => a.index - b.index
        )
      : [];
  const facilityCourseCount = facility?.courses?.count ?? 0;
  const courseLabel =
    coursePreferences.length === 0 || facilityCourseCount <= 1
      ? null
      : coursePreferences
          .map((preference) =>
            preference.course?.name
              ? preference.course.name
              : preference.courseBucket?.name
              ? t`Any ${courseSingularLabel} in ${preference.courseBucket.name}`
              : t`Any ${courseSingularLabel}`
          )
          .join(", ");

  if (!isFullyPrivate) return null;
  if (!isPatronRequestSeries && !canManageRecurrence && !pendingRecurrence)
    return null;

  if (placement === "details") {
    if (!pendingRecurrence && !hasActivePersistedSeries) return null;
    if (!activeRecurrence) return null;

    const unavailableCount = unavailableDates.length;
    const removedCount = removedOccurrences.length;
    const hasPreviewChanges =
      !!pendingRecurrence && (unavailableCount > 0 || removedCount > 0);
    const patternLabel = recurrencePatternLabel({
      recurrenceType: activeRecurrence.recurrenceType,
      repeatInterval: activeRecurrence.repeatInterval,
      sourceDate,
    });

    return (
      <VStack gap={1}>
        <DetailRow
          icon={<RecurringIcon />}
          label={t`Recurrence`}
          value={patternLabel}
        />
        {hasPreviewChanges ? (
          <Pressable
            testID="recurring-request-conflicts-banner"
            accessibilityLabel={
              unavailableCount > 0
                ? unavailableCount === 1
                  ? t`1 date is unavailable. Tap to see which.`
                  : t`${unavailableCount} dates are unavailable. Tap to see which.`
                : removedCount === 1
                ? t`1 future request will be removed. Tap to see which.`
                : t`${removedCount} future requests will be removed. Tap to see which.`
            }
            role="button"
            onPress={() => setIsSeriesOpen(true)}
          >
            <HStack
              alignItems="center"
              gap={2}
              padding={3}
              borderRadius="2"
              backgroundColor="surface-main-secondary-subtlest"
            >
              <InfoIcon width={20} height={20} color="icon-main-secondary" />
              <VStack flex={1} gap={0}>
                <Text variant="mainSmallSemiBold" color="text-primary">
                  {availableOccurrences.length === 1
                    ? t`1 of ${totalSeriesDates} dates will be requested`
                    : t`${availableOccurrences.length} of ${totalSeriesDates} dates will be requested`}
                </Text>
                <Text variant="mainSmall" color="text-main-secondary">
                  {unavailableCount > 0 && removedCount > 0
                    ? t`${unavailableCount} dates are unavailable; ${removedCount} future requests will be removed.`
                    : unavailableCount > 0
                    ? unavailableCount === 1
                      ? t`1 date is unavailable. Tap to see which.`
                      : t`${unavailableCount} dates are unavailable. Tap to see which.`
                    : removedCount === 1
                    ? t`1 future request will be removed. Tap to see which.`
                    : t`${removedCount} future requests will be removed. Tap to see which.`}
                </Text>
              </VStack>
              <ChevronRightIcon
                width={16}
                height={16}
                color="icon-main-secondary"
              />
            </HStack>
          </Pressable>
        ) : null}
        {endsDateLabel ? (
          <DetailRow
            icon={<CalendarIcon />}
            label={t`Ends`}
            value={endsDateLabel}
          />
        ) : null}
        <RecurringRequestDatesModal
          savedBookingId={
            !pendingRecurrence && isPatronRequestSeries ? booking.id : undefined
          }
          request={
            booking.__typename === "StandardBooking" ? booking.request : null
          }
          recurrenceType={activeRecurrence.recurrenceType}
          repeatInterval={activeRecurrence.repeatInterval}
          courseLabel={courseLabel}
          availableOccurrences={availableOccurrences}
          unavailableDates={unavailableDates}
          removedOccurrences={removedOccurrences}
          isOpen={isSeriesOpen}
          onClose={() => setIsSeriesOpen(false)}
        />
      </VStack>
    );
  }

  const hasRecurrenceConfigured =
    hasActivePersistedSeries || !!pendingRecurrence;
  // The backend rejects a cadence (recurrence type/interval) change once prior
  // occurrences exist in a persisted series, since changing it would misrepresent
  // requests already made under the old pattern. Only relevant when saving directly
  // against a persisted series -- a pending series has no prior occurrences.
  const lockCadence =
    isPatronRequestSeries &&
    !!sourceDate &&
    seriesBookings.some(
      (seriesBooking) =>
        (seriesBooking.startSlot?.agenda?.date ?? "") < sourceDate
    );
  const seriesPreviewCount = pendingRecurrence
    ? requestCount
    : isPatronRequestSeries
    ? submittedRequestCount
    : requestCount;
  const seriesPreviewOccurrences = pendingRecurrence
    ? availableOccurrences
    : isPatronRequestSeries
    ? persistedOccurrences
    : availableOccurrences;
  const seriesPreviewUnavailableDates = pendingRecurrence
    ? unavailableDates
    : [];
  const seriesPreviewRemovedOccurrences = pendingRecurrence
    ? removedOccurrences
    : [];

  const recurrenceFormValue =
    pendingRecurrence ??
    (hasActivePersistedSeries && activeRecurrence
      ? {
          ...activeRecurrence,
          endDate: series?.effectiveEndDate ?? undefined,
        }
      : undefined);

  const savePendingRecurrence = (nextValue: RecurrenceFormValue) => {
    const nextPendingRecurrence: RecurrenceFormValue =
      nextValue.endCondition === "ON_DATE"
        ? {
            recurrenceType: nextValue.recurrenceType,
            repeatInterval: nextValue.repeatInterval,
            endCondition: "ON_DATE",
            endDate: nextValue.endDate,
          }
        : {
            recurrenceType: nextValue.recurrenceType,
            repeatInterval: nextValue.repeatInterval,
            endCondition: "AFTER_OCCURRENCES",
            occurrenceCount: nextValue.occurrenceCount ?? 4,
          };

    navigation.setParams({
      params: {
        ...route.params?.params,
        pendingRecurrence: nextPendingRecurrence,
      },
    });
  };

  if (
    !canManageRecurrence &&
    !(hasRecurrenceConfigured && seriesPreviewCount > 1)
  ) {
    return null;
  }

  return (
    <>
      <VStack gap={4}>
        {hasRecurrenceConfigured &&
        seriesPreviewCount > 1 &&
        (!pendingRecurrence || availabilityPreview) ? (
          <Button
            variant="secondary"
            size="small"
            accessibilityLabel={t`View entire request series`}
            onPress={() => {
              trackEvent("RecurringRequest.SeriesViewed", {
                bookingId: booking.id,
                seriesCount: seriesPreviewCount,
              });
              setIsSeriesOpen(true);
            }}
          >
            {t`View Entire Series (${seriesPreviewCount})`}
          </Button>
        ) : null}

        {canManageRecurrence ? (
          <Button
            variant="secondary"
            size="small"
            accessibilityLabel={
              hasRecurrenceConfigured
                ? t`Edit request recurrence`
                : t`Make request recurring`
            }
            onPress={() => {
              trackEvent("RecurringRequest.FormOpened", {
                bookingId: booking.id,
                mode: hasRecurrenceConfigured ? "edit" : "create",
              });
              setIsRecurrenceOpen(true);
            }}
          >
            {hasRecurrenceConfigured ? t`Edit Recurrence` : t`Make Recurring`}
          </Button>
        ) : null}
      </VStack>

      <RecurringRequestDatesModal
        savedBookingId={
          !pendingRecurrence && isPatronRequestSeries ? booking.id : undefined
        }
        request={
          booking.__typename === "StandardBooking" ? booking.request : null
        }
        recurrenceType={activeRecurrence?.recurrenceType}
        repeatInterval={activeRecurrence?.repeatInterval}
        courseLabel={courseLabel}
        availableOccurrences={seriesPreviewOccurrences}
        unavailableDates={seriesPreviewUnavailableDates}
        removedOccurrences={seriesPreviewRemovedOccurrences}
        isOpen={isSeriesOpen}
        onClose={() => setIsSeriesOpen(false)}
      />

      {sourceDate && maximumEndDate ? (
        <RecurringRequestModal
          allowSingleOccurrence={isPatronRequestSeries}
          isOpen={isRecurrenceOpen}
          lockCadence={lockCadence}
          maximumEndDate={maximumEndDate}
          clubWeekStartsOn={clubWeekStartsOn}
          sourceDate={sourceDate}
          initialValue={recurrenceFormValue}
          onSavePending={savePendingRecurrence}
          onClose={() => setIsRecurrenceOpen(false)}
        />
      ) : null}
    </>
  );
};

const RecurringRequestSeriesCardFragmentString = graphql`
  fragment RecurringRequestSeriesCard_booking on Booking {
    id
    __typename
    ownerClient {
      ... on Member {
        id
      }
      ... on Customer {
        id
      }
    }
    startSlot {
      __typename
      agenda {
        date
        facility {
          ...useFacilityCourseNoun_facility
          club {
            calendarStartDay
            supportsPrivate
            supportsPublic
          }
          type
          courses {
            count
          }
          patronRecurringRequestMaximumEndDate
          privateBookingPermissionSet {
            membersCanMakeRecurringRequests
          }
        }
      }
    }
    ... on StandardBooking {
      status
      request {
        time
        minTime
        maxTime
        requestCoursePreferences {
          index
          course {
            id
            name
          }
          courseBucket {
            id
            name
          }
        }
      }
    }
    bookingSeries {
      id
      recurrenceType
      repeatInterval
      endCondition
      effectiveEndDate
      requestedOccurrenceCount
      bookings(first: 366, orderBy: [{ field: DATE_TIME, direction: DESC }]) {
        edges {
          node {
            id
            startSlot {
              agenda {
                date
              }
            }
          }
        }
      }
    }
    ... on StandardBooking {
      seriesCount
    }
  }
`;
